import * as Matter from 'matter-js';
import type { I18n } from '@learnforge/i18n';
import type { MechKey } from '../locales';

export interface PaletteOptions {
  el: HTMLElement;
  i18n: I18n<MechKey>;
  world: Matter.World;
  canvas: HTMLCanvasElement;
  // While a shape is "armed" we yank the mouse constraint out of the world so it cannot
  // grab anything; the spawn click is then unambiguously a place-the-shape gesture.
  mouseConstraint: Matter.MouseConstraint;
  onSpawn?(body: Matter.Body): void;
}

type ShapeKind = 'box' | 'circle' | 'ramp' | 'ground';

const SHAPES: { kind: ShapeKind; labelKey: MechKey }[] = [
  { kind: 'box', labelKey: 'palette.box' },
  { kind: 'circle', labelKey: 'palette.circle' },
  { kind: 'ramp', labelKey: 'palette.ramp' },
  { kind: 'ground', labelKey: 'palette.ground' },
];

function spawnAt(kind: ShapeKind, x: number, y: number): Matter.Body {
  switch (kind) {
    case 'box':
      return Matter.Bodies.rectangle(x, y, 50, 50, { mass: 2 });
    case 'circle':
      return Matter.Bodies.circle(x, y, 25, { mass: 2, restitution: 0.6 });
    case 'ramp':
      return Matter.Bodies.rectangle(x, y, 150, 12, {
        isStatic: true,
        angle: -0.3,
        friction: 0.3,
      });
    case 'ground':
      return Matter.Bodies.rectangle(x, y, 200, 16, { isStatic: true });
  }
}

export function mountPalette(opts: PaletteOptions): { destroy(): void } {
  opts.el.classList.add('palette');
  opts.el.innerHTML = '';

  const hint = document.createElement('div');
  hint.className = 'palette-hint';
  hint.textContent = opts.i18n.t('palette.hint');
  opts.el.appendChild(hint);

  let armedKind: ShapeKind | null = null;
  let mouseDetached = false;
  const buttons: HTMLButtonElement[] = [];

  const detachMouse = () => {
    if (mouseDetached) return;
    Matter.World.remove(opts.world, opts.mouseConstraint);
    mouseDetached = true;
  };

  const reattachMouse = () => {
    if (!mouseDetached) return;
    Matter.World.add(opts.world, opts.mouseConstraint);
    mouseDetached = false;
  };

  const disarm = () => {
    armedKind = null;
    for (const b of buttons) b.classList.remove('armed');
    opts.canvas.style.cursor = '';
    reattachMouse();
  };

  for (const s of SHAPES) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'palette-btn';
    btn.dataset.kind = s.kind;
    btn.textContent = opts.i18n.t(s.labelKey);
    btn.addEventListener('click', () => {
      if (armedKind === s.kind) {
        disarm();
        return;
      }
      armedKind = s.kind;
      for (const b of buttons) b.classList.remove('armed');
      btn.classList.add('armed');
      opts.canvas.style.cursor = 'crosshair';
      detachMouse();
    });
    buttons.push(btn);
    opts.el.appendChild(btn);
  }

  const onCanvasMousedown = (e: MouseEvent) => {
    if (!armedKind) return;
    const rect = opts.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const body = spawnAt(armedKind, x, y);
    Matter.World.add(opts.world, body);
    opts.onSpawn?.(body);
    disarm();
  };
  opts.canvas.addEventListener('mousedown', onCanvasMousedown);

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') disarm();
  };
  window.addEventListener('keydown', onKeydown);

  const unsubscribe = opts.i18n.subscribe(() => {
    hint.textContent = opts.i18n.t('palette.hint');
    for (const btn of buttons) {
      const k = btn.dataset.kind as ShapeKind | undefined;
      const found = SHAPES.find((s) => s.kind === k);
      if (found) btn.textContent = opts.i18n.t(found.labelKey);
    }
  });

  return {
    destroy() {
      opts.canvas.removeEventListener('mousedown', onCanvasMousedown);
      window.removeEventListener('keydown', onKeydown);
      disarm();
      unsubscribe();
    },
  };
}
